// frontend/src/components/reputation/OfficeCompactCard.jsx
import React, { useState, useEffect } from 'react';
import { MapPin } from 'lucide-react';
import { fetchGeogridData } from '../../services/geogridCache';
import { formatShortEdtDate } from './OfficeVisibilityBlock';
import { useUiLanguage, perevesti } from '../../context/UiLanguageContext';

export const OfficeCompactCard = ({
  office,
  isSelected,
  onSelect,
  className = '',
}) => {
  const { lang: rawLang, t: contextT } = useUiLanguage();
  const lang = rawLang || 'ru';
  const isEn = lang === 'en';
  const t = (k, v) => (rawLang ? contextT(k, v) : perevesti('ru', k, v));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    if (!office?.kluch) return;
    if (data) return;

    setLoading(true);
    fetchGeogridData(office.kluch)
      .then(({ geogrid }) => {
        if (!isMounted) return;
        setData(geogrid);
        setLoading(false);
      })
      .catch(() => {
        if (!isMounted) return;
        setData(null);
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [office?.kluch, isSelected, data]);

  const isMeasured = Boolean(data?.status === 'measured' && data?.payload?.run);
  const c2Reputation = data?.c2_reputation;
  const firstKw = isMeasured && Array.isArray(data?.payload?.run?.keywords)
    ? data.payload.run.keywords[0]
    : null;

  const top3Share = firstKw?.metrics?.unweightedTop3Share;
  const sharePct = typeof top3Share === 'number'
    ? `${(top3Share * 100).toFixed(1)}%`
    : '—';

  const rawDate =
    data?.payload?.run?.finishedAt ||
    data?.payload?.run?.completedAt ||
    data?.payload?.run?.startedAt ||
    data?.received_at;
  const dateFormatted = formatShortEdtDate(rawDate, lang);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect?.();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      aria-label={t('rep.office.aria_label', { name: office.nazvanie })}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      className={`p-4 rounded-xl flex flex-col justify-between space-y-2.5 text-left transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#C8A653] ${
        isSelected
          ? 'border-2 border-[#C8A653] bg-[#FCFBF8] shadow-xs'
          : 'border border-[#E5E1D6] bg-white hover:border-[#C8A653]/60 shadow-2xs'
      } ${className}`}
    >
      <div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-bold text-[#20231F]">{office.nazvanie}</span>
          <div className="flex items-center gap-1.5">
            {isSelected && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-[#FAF4E5] text-[#A97F2E] border border-[#E5D7B5]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#C8A653]" />
                <span>{t('rep.office.selected')}</span>
              </span>
            )}
            <span
              className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                office.is_main
                  ? 'bg-[#FAF4E5] text-[#A97F2E]'
                  : 'bg-[#F1EEE4] text-[#687077]'
              }`}
            >
              {office.badge}
            </span>
          </div>
        </div>

        <div className="text-xs text-[#687077] mt-1.5 flex items-start gap-1.5">
          <MapPin
            className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${
              office.is_main ? 'text-[#C8A653]' : 'text-[#687077]'
            }`}
          />
          <span className="leading-snug">{office.address}</span>
        </div>

        <div className="text-xs text-[#20231F] font-medium mt-1">
          {office.company_name} · {office.phone}
        </div>

        {c2Reputation?.status === 'measured' && c2Reputation.rating !== null && (
          <div data-testid="compact-card-rating" className="text-xs font-semibold text-[#20231F] mt-1 flex items-center gap-1.5" title={c2Reputation.signature}>
            <span className="text-[#A97F2E]">★ {c2Reputation.rating.toFixed(1)}</span>
            <span className="text-[#687077]">({c2Reputation.reviews_count !== null ? c2Reputation.reviews_count : '—'})</span>
            <span className="text-[10px] text-[#8C7A5B]">· Google Maps</span>
          </div>
        )}
      </div>

      <div className="pt-2 border-t border-[#F1EEE4]">
        {loading ? (
          <div className="h-4 bg-[#F1EEE4] rounded animate-pulse w-3/4" />
        ) : isMeasured && firstKw ? (
          <div
            className="text-[11px] font-medium text-[#20231F] flex items-center justify-between gap-1.5"
            title={isEn ? `top-3 ${sharePct} · “${firstKw.keyword}” · scan ${dateFormatted}` : `топ-3 ${sharePct} · «${firstKw.keyword}» · замер ${dateFormatted}`}
          >
            <div className="flex items-center gap-1 min-w-0">
              <span className="shrink-0">{isEn ? 'top-3 ' : 'топ-3 '}</span>
              <span className="font-bold text-[#0B8B5E] shrink-0">{sharePct}</span>
              <span className="text-[#687077] truncate"> · «{firstKw.keyword}»</span>
            </div>
            {dateFormatted && (
              <span className="text-[#687077] shrink-0 whitespace-nowrap">
                · {isEn ? 'scan' : 'замер'} {dateFormatted}
              </span>
            )}
          </div>
        ) : (
          <div className="text-[11px] text-[#687077] font-medium">
            {t('rep.office.visibility_not_measured')}
          </div>
        )}
      </div>
    </div>
  );
};

export default OfficeCompactCard;
